//lib
import dayjs from 'dayjs';
import { Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React, { useState, useEffect, useMemo } from 'react';
import Loader from '../../components/loader/loader';
import { formatNumber } from '../../utils/utils';
import { UtilizationDashboardStyled } from './utilization-dashboard.styles';
import DateRangePickerField from '../../components/form-fields/date-range-picker/date-range-picker';
import { Button } from '../../components/button/button';
import { useAuth } from '../../context/AuthContext';

//src
import {
  fetchProjects,
  fetchUsersTime,
  processTimeEntries,
  calculatePercentage,
  calculateTotals,
} from './utilization-utils';
import ProgressBar from '../projects-list/project-details/project-health/components/progress-bar/progress-bar';
import Department from './components/department';
import ExportUserTimesheetModal from './export-user-timesheet-modal/export-user-timesheet-modal';

const UtilizationDashboard = () => {
  const today = new Date();
  const firstDayOfLastMonth = dayjs(new Date(today.getFullYear(), today.getMonth() - 1, 1));
  const lastDayOfLastMonth = dayjs(new Date(today.getFullYear(), today.getMonth(), 0));
  const [dateRange, setDateRange] = useState([firstDayOfLastMonth, lastDayOfLastMonth]);

  const [expanded, setExpanded] = useState('');
  const [departmentData, setDepartmentData] = useState(null);
  const [computedTotals, setComputedTotals] = useState({
    totalBillable: 0,
    totalNonBillable: 0,
  });
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const { userPermissions } = useAuth();
  const isAdmin = userPermissions === 'Admin';

  const { data: processedData, isLoading } = useQuery({
    queryKey: ['dashboardData', dateRange],
    queryFn: async () => {
      const [projectsMap, allTimeEntries] = await Promise.all([
        fetchProjects(),
        fetchUsersTime(dateRange[0], dateRange[1]),
      ]);

      return processTimeEntries(allTimeEntries, projectsMap);
    },
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (processedData) {
      setDepartmentData(processedData);
      setComputedTotals(calculateTotals(processedData));
    }
  }, [processedData]);

  const overallPercentage = useMemo(() => {
    const { totalBillable, totalNonBillable } = computedTotals;
    return calculatePercentage(totalBillable, totalBillable + totalNonBillable);
  }, [computedTotals]);

  const toggleDepartmentState = (department) => {
    setDepartmentData((prevData) => {
      const updatedDept = { ...prevData[department] };
      const newActiveState = !updatedDept.isActive;

      // Update the department's active state
      updatedDept.isActive = newActiveState;

      // Adjust department and agency totals based on the new state
      const memberAdjustment = Object.entries(updatedDept.members).reduce(
        (totals, [key, member]) => {
          const memberBillable = member.billableHours || 0;
          const memberNonBillable = member.nonBillableHours || 0;

          // Toggle member state
          updatedDept.members[key] = {
            ...member,
            isActive: newActiveState,
          };

          // Adjust totals only if the new state is false (turning off)
          if (!newActiveState && member.isActive) {
            totals.totalBillable -= memberBillable;
            totals.totalNonBillable -= memberNonBillable;
          } else if (newActiveState && !member.isActive) {
            // If turning on, add member hours
            totals.totalBillable += memberBillable;
            totals.totalNonBillable += memberNonBillable;
          }

          return totals;
        },
        { totalBillable: 0, totalNonBillable: 0 },
      );

      // Update agency totals
      setComputedTotals((prevTotals) => ({
        totalBillable: prevTotals.totalBillable + memberAdjustment.totalBillable,
        totalNonBillable: prevTotals.totalNonBillable + memberAdjustment.totalNonBillable,
      }));

      return { ...prevData, [department]: updatedDept };
    });
  };

  const toggleMemberState = (department, memberKey) => {
    setDepartmentData((prevData) => {
      const updatedDept = { ...prevData[department] };
      const updatedMember = { ...updatedDept.members[memberKey] };

      const memberBillable = updatedMember.billableHours || 0;
      const memberNonBillable = updatedMember.nonBillableHours || 0;

      // Toggle the member's active state
      updatedMember.isActive = !updatedMember.isActive;

      // Adjust department and agency totals
      if (updatedMember.isActive) {
        updatedDept.totalBillableHours += memberBillable;
        updatedDept.totalNonBillableHours += memberNonBillable;
      } else {
        updatedDept.totalBillableHours -= memberBillable;
        updatedDept.totalNonBillableHours -= memberNonBillable;
      }

      // Adjust agency totals
      setComputedTotals((prevTotals) => {
        const adjustment = updatedMember.isActive
          ? {
              totalBillable: prevTotals.totalBillable + memberBillable,
              totalNonBillable: prevTotals.totalNonBillable + memberNonBillable,
            }
          : {
              totalBillable: prevTotals.totalBillable - memberBillable,
              totalNonBillable: prevTotals.totalNonBillable - memberNonBillable,
            };
        return adjustment;
      });

      updatedDept.members[memberKey] = updatedMember;
      return { ...prevData, [department]: updatedDept };
    });
  };

  return (
    <UtilizationDashboardStyled>
      <div className="header">
        <Typography variant="h4" className="title">
          Company Utilization
        </Typography>
        <div className="dateRange">
          <Typography variant="body2" style={{ fontWeight: '500' }}>
            Timespan:
          </Typography>
          <DateRangePickerField
            name="dateRange"
            value={dateRange}
            onChange={setDateRange}
            defaultValue={dateRange}
          />
          {isAdmin && (
            <Button
              variant="outlined"
              sx={{ width: '150px' }}
              onClick={() => setIsExportModalOpen(true)}
            >
              Export User Timesheet
            </Button>
          )}
        </div>
      </div>

      {isAdmin && (
        <ExportUserTimesheetModal
          open={isExportModalOpen}
          onClose={() => setIsExportModalOpen(false)}
          defaultDateRange={dateRange}
        />
      )}

      {isLoading ? (
        <Loader absolute />
      ) : (
        <div className="barContainer">
          <div className="totalProgressBar">
            <ProgressBar
              value={overallPercentage}
              startLabel="Agency (Billable / Non Billable)"
              middleLabel={`${formatNumber(
                computedTotals.totalBillable,
              )}h / ${formatNumber(computedTotals.totalNonBillable)}h`}
              endLabel={`${overallPercentage}%`}
            />
          </div>
          {departmentData &&
            Object.entries(departmentData).map(([department, deptData]) => (
              <Department
                key={department}
                department={department}
                deptData={deptData}
                expanded={expanded}
                setExpanded={setExpanded}
                toggleDepartmentState={toggleDepartmentState}
                toggleMemberState={toggleMemberState}
              />
            ))}
        </div>
      )}
    </UtilizationDashboardStyled>
  );
};

export default UtilizationDashboard;
